// #region imports

// --- Angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

// --- RxJs
import { BehaviorSubject, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

// --- HS Code

// #endregion

@Component({
  selector: 'hs-inline-editor',
  templateUrl: './inline-editor.component.html',
  styleUrls: ['./inline-editor.component.scss']
})
export class InlineEditorComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  private _defaultMaximumInputLength = 1000;

  @Input() label: string | null = '';
  @Input() identifier: string | null = '';
  @Input() initialValue: string | null = '';
  @Input() placeholder: string | null = '';
  @Input() required: boolean | null = false;
  @Input() maxLength: number | null = this._defaultMaximumInputLength;
  @Input() useButtons: boolean | null = true ;
  @Input() tooltipText: string | null = '';

  @Input() set editMode(value: boolean) {
    this.editMode$.next(value);
  }

  @Output() update = new EventEmitter<string>();
  @Output() save = new EventEmitter<string>();
  @Output() fieldFocus = new EventEmitter();
  @Output() fieldBlur = new EventEmitter();
  @Output() editModeUpdate = new EventEmitter<boolean>();

  @ViewChild('formControlRef', {static: true})
  private _formControlRef: ElementRef<HTMLInputElement> | undefined;

  editMode$: BehaviorSubject<boolean>;
  formControl: FormControl;

  private _editModeSubscription: Subscription = Subscription.EMPTY;
  private _formControlSubscription: Subscription = Subscription.EMPTY;

  constructor() {
    this.editMode$ = new BehaviorSubject(false);
    this.formControl = new FormControl('', Validators.required);
  }

  ngOnInit() {

  }

  ngAfterViewInit() {
    this._editModeSubscription = this.editMode$
    .pipe(
      distinctUntilChanged(),
    )
    .subscribe((editMode) => {
      if (editMode) {
        this.focusOnFormControlRef();
      }
      this.editModeUpdate.emit(editMode);
    });

    this._formControlSubscription = this.formControl.valueChanges
      .pipe(
        distinctUntilChanged(),
      )
      .subscribe((value: string) => {
        this.update.emit(value);
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['initialValue'] && changes['initialValue'].currentValue) {
      this.formControl.setValue(changes['initialValue'].currentValue);
    }
    if (changes && changes['required'] && changes['required'].currentValue) {
      this._updateValidation();
    }
    if (changes && changes['maxCharacters'] && changes['maxCharacters'].currentValue) {
      this._updateValidation();
    }
  }
  ngOnDestroy(): void {
    if (this._editModeSubscription) {
      this._editModeSubscription.unsubscribe();
    }
    if (this._formControlSubscription) {
      this._formControlSubscription.unsubscribe();
    }
  }

  toggleEditMode(value = true) {
    this.editMode$.next(value);
  }

  focusOnFormControlRef() {
    setTimeout(() => {
      this._formControlRef?.nativeElement.focus();
    });
  }

  onSave() {
    this.toggleEditMode(false);
    this.save.emit(this.formControl.value);
  }

  onFieldClick(): void {
    if (!this.useButtons) {
      this.editMode$.next(true);
    }
  }

  onFieldFocus(): void {
    this.fieldFocus.emit();
  }

  onFieldBlur(): void {
    this.fieldBlur.emit(this.formControl.value);
    if (!this.useButtons && this.formControl.valid) {
      this.editMode$.next(false);
    }
  }

  private _updateValidation() {
    const rules = [];
    if (this.required) {
      rules.push(Validators.required);
    }
    if (this.maxLength) {
      rules.push(Validators.maxLength(this.maxLength));
    }
    this.formControl.setValidators(rules);
    this.formControl.updateValueAndValidity();
  }
}
